import { useEffect, useRef } from "react";

const POLL_INTERVAL_MS = 120_000;

interface BuildEnv {
  BUILD_CREATED_AT?: string;
}

interface BuildVersionWatcherProps {
  buildCreatedAt?: string | null;
}

export function BuildVersionWatcher({
  buildCreatedAt,
}: BuildVersionWatcherProps) {
  const isReloadingRef = useRef(false);
  const intervalIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    if (!buildCreatedAt || buildCreatedAt === "dev") {
      return;
    }

    const checkBuildVersion = async () => {
      if (document.hidden) {
        return;
      }

      try {
        const response = await fetch("/version.json", {
          cache: "no-store",
        });

        if (!response.ok) {
          return;
        }

        const env = (await response.json()) as BuildEnv;
        const nextBuildCreatedAt = env.BUILD_CREATED_AT ?? null;

        if (!nextBuildCreatedAt) {
          return;
        }

        if (nextBuildCreatedAt !== buildCreatedAt && !isReloadingRef.current) {
          isReloadingRef.current = true;
          window.location.reload();
        }
      } catch (error) {
        console.error("Failed to check build version", error);
      }
    };

    const startPolling = () => {
      if (intervalIdRef.current !== null) {
        return;
      }

      intervalIdRef.current = window.setInterval(() => {
        void checkBuildVersion();
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalIdRef.current === null) {
        return;
      }

      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }

      void checkBuildVersion();
      startPolling();
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [buildCreatedAt]);

  return null;
}
